<script setup>
import { computed, ref } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  messageId: {
    type: Number,
    required: true,
  },
  conversationId: {
    type: Number,
    required: true,
  },
  callData: {
    type: Object,
    required: true,
  },
});

const store = useStore();

const hasActiveCall = useMapGetter('whatsappCall/hasActiveCall');
const activeCall = useMapGetter('whatsappCall/activeCall');
const isMuted = useMapGetter('whatsappCall/isMuted');
const callDuration = useMapGetter('whatsappCall/callDuration');
const isConnected = useMapGetter('whatsappCall/isConnected');

// Check if this message is for the active call
const isActiveCall = computed(() => {
  if (!hasActiveCall.value || !activeCall.value) return false;
  return activeCall.value.callId === props.callData.call_id;
});

const formattedDuration = computed(() => {
  const seconds = callDuration.value;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
});

const toggleMute = () => {
  store.dispatch('whatsappCall/toggleMute');
  // Apply mute to Janus plugin
  const pluginHandle = store.state.whatsappCall.janusPluginHandle;
  if (pluginHandle) {
    if (isMuted.value) {
      pluginHandle.unmuteAudio();
    } else {
      pluginHandle.muteAudio();
    }
  }
};

const endCall = async () => {
  await store.dispatch('whatsappCall/endCall');
  // TODO: Send hangup request to WhatsApp API
};

const volumeLevel = ref(80);

const adjustVolume = (event) => {
  volumeLevel.value = event.target.value;
  // TODO: Apply volume to audio element
};
</script>

<template>
  <div v-if="isActiveCall" class="w-full max-w-2xl mt-2">
    <!-- Active call audio bar -->
    <div class="bg-n-teal-1 border border-n-teal-6 rounded-lg p-4 shadow-sm">
      <!-- Header with status -->
      <div class="flex items-center justify-between mb-3">
        <div class="flex items-center gap-2">
          <div class="size-2 rounded-full bg-n-teal-9 animate-pulse"></div>
          <span class="text-sm font-medium text-n-teal-11">
            {{ isConnected ? 'Connected' : 'Connecting...' }}
          </span>
        </div>
        <span class="text-lg font-semibold text-n-slate-12">{{ formattedDuration }}</span>
      </div>

      <!-- Waveform visualization -->
      <div class="flex items-center gap-1 h-12 mb-4">
        <div
          v-for="i in 40"
          :key="i"
          class="flex-1 bg-n-teal-9 rounded-full transition-all duration-150"
          :style="{
            height: `${Math.random() * 100}%`,
            opacity: isConnected ? 0.7 : 0.3
          }"
        ></div>
      </div>

      <!-- Controls -->
      <div class="flex items-center justify-between gap-3">
        <!-- Volume control -->
        <div class="flex items-center gap-2 flex-1">
          <Icon
            :icon="volumeLevel > 50 ? 'i-ph-speaker-high' : 'i-ph-speaker-low'"
            class="size-5 text-n-slate-11"
          />
          <input
            v-model="volumeLevel"
            type="range"
            min="0"
            max="100"
            class="flex-1 h-2 bg-n-slate-3 rounded-lg appearance-none cursor-pointer"
            @input="adjustVolume"
          >
          <span class="text-xs text-n-slate-11 w-8">{{ volumeLevel }}%</span>
        </div>

        <!-- Action buttons -->
        <div class="flex items-center gap-2">
          <!-- Mute button -->
          <button
            class="rounded-lg p-2.5 transition-colors"
            :class="isMuted ? 'bg-n-ruby-9 text-white' : 'bg-n-slate-3 text-n-slate-11 hover:bg-n-slate-4'"
            :title="isMuted ? 'Unmute' : 'Mute'"
            @click="toggleMute"
          >
            <Icon :icon="isMuted ? 'i-ph-microphone-slash' : 'i-ph-microphone'" class="size-5" />
          </button>

          <!-- End call button -->
          <button
            class="bg-n-ruby-9 hover:bg-n-ruby-10 text-white rounded-lg py-2.5 px-4 flex items-center gap-2 font-medium transition-colors"
            @click="endCall"
          >
            <Icon icon="i-ph-phone-x" class="size-5" />
            End Call
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
input[type="range"]::-webkit-slider-thumb {
  appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--n-teal-9);
  cursor: pointer;
}

input[type="range"]::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--n-teal-9);
  cursor: pointer;
  border: none;
}

@keyframes pulse {
  0%, 100% {
    opacity: 0.3;
  }
  50% {
    opacity: 1;
  }
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
</style>
